import { Head } from '@inertiajs/react'
import { useEffect } from 'react'
import PageHeader from '@/components/common/PageHeader'
import PatientHamburgerMenu from '@/components/common/PatientHamburgerMenu'
import MedicationMailNotice from '@/components/reservations/MedicationMailNotice'
import PrescriptionNotice from '@/components/reservations/PrescriptionNotice'
import type { MedicalDepartment, Clinic, DoctorSchedule, PatientFamilyProfile } from '@/types/schedule'

type Pharmacy = {
  id: number
  name: string
  address: string
}

type Examination = {
  id: number
  medical_department: MedicalDepartment
  clinic: Clinic
  doctor_schedule: DoctorSchedule
  patient_family_profile: PatientFamilyProfile | null
  pharmacy: Pharmacy | null
  is_medication_mail: boolean
  web_mtg_url: string
  created_at: string
}

type Props = {
  examination: Examination
  insurance_card_registered: boolean
  insurance_registration_path: string | null
  medication_mail_delivery_estimate: string
}

export default function Complete({
  examination,
  insurance_card_registered,
  insurance_registration_path,
  medication_mail_delivery_estimate,
}: Props) {
  // 予約完了後のブラウザバックを防止し、オンライン診療TOPへリダイレクト
  useEffect(() => {
    window.history.pushState(null, '', window.location.href)
    const handlePopState = () => {
      window.location.href = '/online_clinic'
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  return (
    <>
      <Head title="予約完了 - オンライン診療" />

      <div className="min-h-screen bg-white">
        <PageHeader trailing={<PatientHamburgerMenu />} subtitle="予約完了" />

        {/* Main Content */}
        <main className="mx-auto max-w-3xl px-4 py-6">
          <div className="space-y-6">
            {/* 完了メッセージ */}
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">予約が完了しました</h2>
              <p className="text-gray-600">
                ご予約いただきありがとうございます。
                <br />
                予約内容の詳細をメールでお送りしました。
              </p>
            </div>

            {/* 資格情報未登録の場合の登録導線 */}
            {!insurance_card_registered && insurance_registration_path && (
              <div className="bg-red-100 border border-red-400 rounded-lg p-4">
                <p className="text-sm font-bold text-red-800">
                  診察時間までに、会員情報ページから医療保険資格情報を登録してください。
                </p>
                <a
                  href={insurance_registration_path}
                  className="mt-2 inline-block text-sm font-medium text-red-700 underline"
                >
                  会員情報ページで医療保険資格情報を登録する
                </a>
              </div>
            )}

            {/* 予約内容 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">予約内容</h3>

              <dl className="space-y-4">
                <div className="flex border-b pb-3">
                  <dt className="w-1/3 text-gray-500 text-sm">患者名</dt>
                  <dd className="w-2/3 text-gray-800">{examination.patient_family_profile?.full_name || '-'}</dd>
                </div>

                <div className="flex border-b pb-3">
                  <dt className="w-1/3 text-gray-500 text-sm">診療科目</dt>
                  <dd className="w-2/3 text-gray-800">{examination.medical_department.name}</dd>
                </div>

                <div className="flex border-b pb-3">
                  <dt className="w-1/3 text-gray-500 text-sm">クリニック</dt>
                  <dd className="w-2/3 text-gray-800">{examination.clinic.name}</dd>
                </div>

                <div className="flex border-b pb-3">
                  <dt className="w-1/3 text-gray-500 text-sm">診療日時</dt>
                  <dd className="w-2/3 text-gray-800">
                    <div>{examination.doctor_schedule.formatted_date}</div>
                    <div className="text-cyan-600 font-medium">{examination.doctor_schedule.formatted_time}</div>
                    <PrescriptionNotice
                      nightSlot={examination.doctor_schedule.night_slot}
                      expectedPrescriptionIssuedDate={examination.doctor_schedule.expected_prescription_issued_date}
                      pharmacyReceivableFrom={
                        examination.is_medication_mail ? null : examination.doctor_schedule.pharmacy_receivable_from
                      }
                      className="mt-1"
                    />
                  </dd>
                </div>

                <div className="flex border-b pb-3">
                  <dt className="w-1/3 text-gray-500 text-sm">担当医師</dt>
                  <dd className="w-2/3 text-gray-800">{examination.doctor_schedule.doctor.full_name}</dd>
                </div>

                <div className="flex">
                  <dt className="w-1/3 text-gray-500 text-sm">処方せん受取</dt>
                  <dd className="w-2/3 text-gray-800">
                    {examination.is_medication_mail ? (
                      <span className="text-cyan-600">おくすりおうち便（配送料900円）</span>
                    ) : examination.pharmacy ? (
                      <div>
                        <div>{examination.pharmacy.name}</div>
                        <div className="text-sm text-gray-500">{examination.pharmacy.address}</div>
                      </div>
                    ) : (
                      '-'
                    )}
                  </dd>
                </div>
              </dl>
              {examination.is_medication_mail && (
                <MedicationMailNotice className="mt-4" deliveryEstimate={medication_mail_delivery_estimate} />
              )}
            </div>

            {/* 注意事項 */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-medium text-yellow-800 mb-2">診療前のご確認</h3>
              <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                <li>診療開始5分前からビデオ通話に参加可能です</li>
                <li>安定したインターネット環境でご参加ください</li>
                <li>カメラ・マイクが使用できることをご確認ください</li>
                <li>身分証明書をお手元にご用意ください</li>
              </ul>
            </div>

            {/* キャンセルについて */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-medium text-red-800 mb-2">キャンセルについて</h3>
              <p className="text-sm text-red-700">
                ご予定が合わなくなった場合、予約確認ページからキャンセルが可能です。
                <br />
                ※診療開始2時間前を過ぎてからのキャンセルは、キャンセル料2,000円が発生します。
              </p>
              <a
                href={`/online_clinic/examinations/${examination.id}`}
                className="mt-3 block w-full py-2 px-4 bg-white border border-red-300 text-red-700 text-center rounded-lg hover:bg-red-100 transition"
              >
                予約確認ページへ
              </a>
            </div>

            {/* ボタン */}
            <div className="space-y-3">
              <a
                href={`/online_clinic/examinations/${examination.id}`}
                className="block w-full py-3 px-4 bg-cyan-500 text-white text-center rounded-lg hover:bg-cyan-600 transition"
              >
                予約詳細を確認する
              </a>

              <a
                href="/online_clinic"
                className="block w-full py-3 px-4 border border-gray-300 text-gray-700 text-center rounded-lg hover:bg-gray-50 transition"
              >
                オンライン診療トップへ
              </a>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
